import { useState, useEffect, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import { Building2, Sparkles, Key, Bell, Phone, Globe, ShoppingCart, Info, Copy, Trash2, Plus, BarChart2, CheckCircle2, Lock, Zap, Upload, Receipt, Link2, AlertTriangle, MapPin, Printer, Usb, Cable, Monitor, Sun, Moon, Laptop, Tablet, CreditCard } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Tooltip } from '@/components/ui/Tooltip'
import { fadeInUp } from '@/lib/motion'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useMyRestaurant, useUpdateRestaurant, useMyUsage, type DeliveryZone, type Plan, type POSMode } from '@/lib/queries/useRestaurants'
import { useApiKeys, useGenerateApiKey, useRevokeApiKey } from '@/lib/queries/useApiKeys'
import { useAppStore } from '@/lib/store/useAppStore'
import { useUIStore } from '@/lib/store/useUIStore'
import i18next, { applyRTL } from '@/lib/i18n'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CountrySelect, COUNTRIES } from '@/components/ui/CountrySelect'
import { GoogleLocationPicker, type LocationSelection } from '@/components/maps/GoogleLocationPicker'
import { uploadImage } from '@/lib/queries/useBookingPageConfig'
import { STRAPI_BASE_URL } from '@/config/api'
import { detectPrinterCapabilities, printReceipt } from '@/lib/pos/receiptPrinter'
import { usePOSPrinterStore, type POSPrinterMethod, type POSReceiptWidth } from '@/lib/store/usePOSPrinterStore'
import { useThemeStore, type ThemeMode } from '@/lib/store/useThemeStore'
import { usePOSPreferencesStore, getPOSRestaurantPreferences, type POSOrderTypeKey } from '@/lib/store/usePOSPreferencesStore'
import type { POSOrder } from '@/lib/types/pos'

type SettingsTab = 'identity' | 'features' | 'appearance' | 'api-keys' | 'notifications' | 'usage'
type RestaurantSettingsTab = 'profile' | 'location' | 'delivery' | 'payments' | 'public-link' | 'receipt'
type DeliveryZoneFormRow = { town: string; aliases: string; postalCodes: string; deliveryFee: string; minimumOrder: string }

function normalizeDeliveryPricing(flatFee?: number, chargePerKm?: number) {
  const deliveryFlatFee = Math.max(0, Number(flatFee ?? 0) || 0)
  const deliveryChargePerKm = deliveryFlatFee > 0
    ? 0
    : Math.max(0, Number(chargePerKm ?? 0) || 0)

  return { deliveryFlatFee, deliveryChargePerKm }
}

export default function SettingsPage() {
  const { t } = useTranslation()
  const { settingsTab: activeTab, setSettingsTab: setActiveTab } = useUIStore()

  const tabs: Array<{ id: SettingsTab; label: string; icon: React.ElementType }> = [
    { id: 'identity', label: t('settings.tabs.identity'), icon: Building2 },
    { id: 'features', label: t('settings.tabs.features'), icon: Sparkles },
    { id: 'appearance', label: 'Appearance', icon: Monitor },
    { id: 'api-keys', label: t('settings.tabs.apiKeys'), icon: Key },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: Bell },
    { id: 'usage', label: 'Plan & Usage', icon: BarChart2 },
  ]

  return (
    <div>
      <PageHeader title={t('settings.title')} description={t('settings.description')} />
      <div className="flex min-w-0 flex-col gap-4 md:flex-row md:gap-6">
        <nav aria-label="Settings sections" className="scrollbar-none flex w-full shrink-0 gap-1 overflow-x-auto pb-1 md:w-48 md:flex-col md:overflow-visible md:pb-0">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'flex min-h-11 w-auto shrink-0 items-center gap-2 rounded-[var(--radius-md)] px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer md:w-full',
                activeTab === id
                  ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)]',
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          {activeTab === 'identity' && <IdentityTab />}
          {activeTab === 'features' && <FeaturesTab />}
          {activeTab === 'appearance' && <AppearanceTab />}
          {activeTab === 'api-keys' && <ApiKeysTab />}
          {activeTab === 'notifications' && <NotificationsTab />}
          {activeTab === 'usage' && <UsageTab />}
        </div>
      </div>
    </div>
  )
}

// ─── Identity Tab ──────────────────────────────────────────────────────────────

const identitySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  currency: z.string().trim().min(1, 'Currency is required').max(8, 'Use a short currency code'),
  languageCode: z.string().optional(),
  timezoneId: z.string().trim().min(1, 'Timezone is required'),
  deliveryFlatFee: z.string().optional(),
  deliveryChargePerKm: z.string().optional(),
  onlinePaymentsEnabled: z.boolean().optional(),
  onlinePaymentsOptional: z.boolean().optional(),
  revolutEnvironment: z.enum(['sandbox', 'production']).optional(),
  revolutProductionApiKey: z.string().optional(),
  revolutProductionPublicKey: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  countryCode: z.string().optional(),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
})
type IdentityForm = z.infer<typeof identitySchema>

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'ar', label: 'العربية' },
  { value: 'de', label: 'Deutsch' },
]

const DEFAULT_TIMEZONE =
  typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    : 'UTC'

const BASE_TIMEZONE_OPTIONS = [
  { value: 'Europe/Dublin', label: 'Europe/Dublin' },
  { value: 'Europe/Rome', label: 'Europe/Rome' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'Europe/Paris', label: 'Europe/Paris' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid' },
  { value: 'Africa/Casablanca', label: 'Africa/Casablanca' },
  { value: 'Africa/Algiers', label: 'Africa/Algiers' },
  { value: 'Africa/Tunis', label: 'Africa/Tunis' },
  { value: 'UTC', label: 'UTC' },
]

function getSupportedTimezoneOptions() {
  const supportedValuesOf = (Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[]
  }).supportedValuesOf

  if (typeof supportedValuesOf !== 'function') return BASE_TIMEZONE_OPTIONS

  try {
    const timezones = supportedValuesOf('timeZone')
    if (!Array.isArray(timezones) || timezones.length === 0) return BASE_TIMEZONE_OPTIONS

    return timezones
      .map((value) => ({ value, label: value }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch {
    return BASE_TIMEZONE_OPTIONS
  }
}

function timezoneOptionsFor(current?: string | null) {
  const options = getSupportedTimezoneOptions()
  for (const value of [current, DEFAULT_TIMEZONE]) {
    if (value && !options.some((option) => option.value === value)) {
      options.unshift({ value, label: value })
    }
  }
  return options
}

function IdentityTab() {
  const { t } = useTranslation()
  const { data: resp, isLoading } = useMyRestaurant()
  const updateRestaurant = useUpdateRestaurant()
  const restaurant = Array.isArray((resp as any)?.data) ? (resp as any).data[0] : null
  const { setLanguageCode } = useAppStore()
  const [restaurantTab, setRestaurantTab] = useState<RestaurantSettingsTab>('profile')
  const [deliveryZones, setDeliveryZones] = useState<DeliveryZoneFormRow[]>([])
  const timezoneOptions = useMemo(
    () => timezoneOptionsFor(restaurant?.timezoneId),
    [restaurant?.timezoneId],
  )

  const restaurantTabs: Array<{ id: RestaurantSettingsTab; label: string; icon: React.ElementType }> = [
    { id: 'profile', label: 'Profile', icon: Building2 },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'delivery', label: 'Delivery', icon: ShoppingCart },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'public-link', label: 'Public link', icon: Link2 },
    { id: 'receipt', label: 'Receipt', icon: Receipt },
  ]

  const form = useForm<IdentityForm>({
    resolver: zodResolver(identitySchema) as any,
    defaultValues: {
      name: '', email: '', phone: '', currency: 'EUR', languageCode: '',
      timezoneId: DEFAULT_TIMEZONE,
      deliveryFlatFee: '',
      deliveryChargePerKm: '',
      onlinePaymentsEnabled: false,
      onlinePaymentsOptional: true,
      revolutEnvironment: 'sandbox',
      revolutProductionApiKey: '',
      revolutProductionPublicKey: '',
      address1: '', address2: '', city: '', postalCode: '', country: '', countryCode: '',
      latitude: '', longitude: '',
    },
  })

  const optionalNumber = (value?: string) => {
    if (!value?.trim()) return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  useEffect(() => {
    if (restaurant) {
      form.reset({
        name: restaurant.name ?? '',
        email: restaurant.email ?? '',
        phone: restaurant.phone ?? '',
        currency: restaurant.currency ?? 'EUR',
        languageCode: restaurant.languageCode ?? '',
        timezoneId: restaurant.timezoneId ?? DEFAULT_TIMEZONE,
        deliveryFlatFee:
          restaurant.deliveryFlatFee != null ? String(restaurant.deliveryFlatFee) : '',
        deliveryChargePerKm:
          restaurant.deliveryChargePerKm != null ? String(restaurant.deliveryChargePerKm) : '',
        onlinePaymentsEnabled: restaurant.onlinePaymentsEnabled === true,
        onlinePaymentsOptional: restaurant.onlinePaymentsOptional !== false,
        revolutEnvironment: restaurant.revolutEnvironment === 'production' ? 'production' : 'sandbox',
        revolutProductionApiKey: '',
        revolutProductionPublicKey: '',
        address1: restaurant.address?.address1 ?? '',
        address2: restaurant.address?.address2 ?? '',
        city: restaurant.address?.city ?? '',
        postalCode: restaurant.address?.postalCode ? String(restaurant.address.postalCode) : '',
        country: restaurant.address?.country ?? '',
        countryCode: restaurant.address?.countryCode ?? '',
        latitude: restaurant.address?.latitude != null ? String(restaurant.address.latitude) : '',
        longitude: restaurant.address?.longitude != null ? String(restaurant.address.longitude) : '',
      })
      setDeliveryZones((Array.isArray(restaurant.deliveryZones) ? restaurant.deliveryZones : []).map((zone: DeliveryZone) => ({
        town: zone.town ?? '',
        aliases: (zone.aliases ?? []).join(', '),
        postalCodes: (zone.postalCodes ?? []).join(', '),
        deliveryFee: zone.deliveryFee == null ? '' : String(zone.deliveryFee),
        minimumOrder: zone.minimumOrder == null ? '' : String(zone.minimumOrder),
      })))
    }
  }, [restaurant?.documentId])

  const onSubmit = (data: IdentityForm) => {
    if (!restaurant) return
    const deliveryPricing = normalizeDeliveryPricing(
      optionalNumber(data.deliveryFlatFee),
      optionalNumber(data.deliveryChargePerKm),
    )
    const payload: Record<string, unknown> = {
      documentId: restaurant.documentId,
      name: data.name,
      email: data.email || undefined,
      phone: data.phone || undefined,
      currency: data.currency.trim().toUpperCase(),
      languageCode: data.languageCode || undefined,
      timezoneId: data.timezoneId || 'UTC',
      deliveryFlatFee: deliveryPricing.deliveryFlatFee,
      deliveryChargePerKm: deliveryPricing.deliveryChargePerKm,
      deliveryZones: deliveryZones
        .filter((zone) => zone.town.trim())
        .map((zone) => ({
          town: zone.town.trim(),
          aliases: zone.aliases.split(',').map((value) => value.trim()).filter(Boolean),
          postalCodes: zone.postalCodes.split(',').map((value) => value.trim()).filter(Boolean),
          deliveryFee: optionalNumber(zone.deliveryFee) ?? null,
          minimumOrder: optionalNumber(zone.minimumOrder) ?? null,
        })),
      onlinePaymentsEnabled: data.onlinePaymentsEnabled === true,
      onlinePaymentsOptional: data.onlinePaymentsOptional !== false,
      onlinePaymentProvider: 'revolut',
      revolutEnvironment: data.revolutEnvironment === 'production' ? 'production' : 'sandbox',
      address: {
        address1: data.address1 || undefined,
        address2: data.address2 || undefined,
        city: data.city || undefined,
        postalCode: data.postalCode || undefined,
        country: data.country || undefined,
        countryCode: data.countryCode || undefined,
        latitude: optionalNumber(data.latitude),
        longitude: optionalNumber(data.longitude),
      },
    }
    if (data.revolutProductionApiKey?.trim()) {
      payload.revolutProductionApiKey = data.revolutProductionApiKey.trim()
    }
    if (data.revolutProductionPublicKey?.trim()) {
      payload.revolutProductionPublicKey = data.revolutProductionPublicKey.trim()
    }
    updateRestaurant.mutate(payload as any, {
      onSuccess: () => {
        toast.success(t('settings.identity.saved'))
        const lang = data.languageCode || 'en'
        setLanguageCode(lang)
        i18next.changeLanguage(lang)
        applyRTL(lang)
      },
    })
  }

  const latitudeValue = optionalNumber(form.watch('latitude'))
  const longitudeValue = optionalNumber(form.watch('longitude'))
  const restaurantLocation =
    latitudeValue != null && longitudeValue != null
      ? { latitude: latitudeValue, longitude: longitudeValue }
      : null

  const applyRestaurantLocation = (location: LocationSelection) => {
    form.setValue('latitude', String(location.latitude), { shouldDirty: true })
    form.setValue('longitude', String(location.longitude), { shouldDirty: true })
    if (location.address) {
      form.setValue('address1', location.address, { shouldDirty: true })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-10 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <motion.div {...fadeInUp} className="space-y-4">
      <div className="flex flex-wrap gap-2 rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-2">
        {restaurantTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setRestaurantTab(id)}
            className={cn(
              'inline-flex items-center gap-2 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium transition-colors',
              restaurantTab === id
                ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]',
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {restaurantTab === 'public-link' ? (
        <PublicLinkCard restaurant={restaurant} />
      ) : restaurantTab === 'receipt' ? (
        <div className="space-y-4">
          <TicketLogoCard restaurant={restaurant} />
          <PrinterSetupCard restaurant={restaurant} />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {restaurantTab === 'profile'
                ? t('settings.identity.title')
                  : restaurantTab === 'location'
                    ? 'Restaurant location'
                    : restaurantTab === 'delivery'
                      ? 'Delivery pricing'
                      : 'Online payments'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {restaurantTab === 'profile' && (
                <>
                  <Input label={t('settings.identity.restaurantName')} error={form.formState.errors.name?.message} {...form.register('name')} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input label={t('settings.identity.email')} type="email" placeholder="contact@restaurant.com" {...form.register('email')} />
                    <Input label={t('settings.identity.phone')} placeholder="+1 234 567 890" {...form.register('phone')} />
                  </div>
                  <Input
                    label="Currency"
                    placeholder="MAD"
                    error={form.formState.errors.currency?.message}
                    {...form.register('currency')}
                  />
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-[var(--color-text-secondary)]">{t('settings.identity.language')}</label>
                    <select
                      {...form.register('languageCode')}
                      className="w-full h-10 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)] cursor-pointer"
                    >
                      {LANGUAGE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-[var(--color-text-secondary)]">Local timezone</label>
                    <select
                      {...form.register('timezoneId')}
                      className="w-full h-10 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)] cursor-pointer"
                    >
                      {timezoneOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {form.formState.errors.timezoneId?.message && (
                      <p className="text-xs text-[var(--color-accent-red)]">{form.formState.errors.timezoneId.message}</p>
                    )}
                  </div>
                </>
              )}

              {restaurantTab === 'location' && (
                <div className="space-y-3">
                  <Input label={t('settings.identity.addressLine1')} placeholder="Street address" {...form.register('address1')} />
                  <Input label={t('settings.identity.addressLine2')} placeholder="Suite, floor, etc." {...form.register('address2')} />
                  <Input label={t('settings.identity.postalCode')} placeholder="12345" {...form.register('postalCode')} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Restaurant latitude"
                      type="number"
                      min="-90"
                      max="90"
                      step="any"
                      placeholder="33.573110"
                      {...form.register('latitude')}
                    />
                    <Input
                      label="Restaurant longitude"
                      type="number"
                      min="-180"
                      max="180"
                      step="any"
                      placeholder="-7.589843"
                      {...form.register('longitude')}
                    />
                  </div>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Required when delivery is charged by distance. Use the restaurant's exact GPS position.
                  </p>
                  <GoogleLocationPicker
                    value={restaurantLocation}
                    onChange={applyRestaurantLocation}
                    searchPlaceholder="Search for your restaurant or address"
                    mapClassName="h-72"
                  />
                  <Controller
                    control={form.control}
                    name="countryCode"
                    render={({ field }) => (
                      <CountrySelect
                        label={t('settings.identity.country')}
                        value={field.value ?? ''}
                        onChange={(code) => {
                          field.onChange(code)
                          form.setValue('country', COUNTRIES.find((c) => c.code === code)?.name ?? '')
                          form.setValue('countryCode', code)
                        }}
                      />
                    )}
                  />
                </div>
              )}

              {restaurantTab === 'delivery' && (
                <div className="space-y-3">
                  <Input
                    label="Flat delivery fee"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    {...form.register('deliveryFlatFee', {
                      onChange: (event) => {
                        if (Number(event.target.value) > 0) form.setValue('deliveryChargePerKm', '0')
                      },
                    })}
                  />
                  <Input
                    label="Charge per km"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    {...form.register('deliveryChargePerKm', {
                      onChange: (event) => {
                        if (Number(event.target.value) > 0) form.setValue('deliveryFlatFee', '0')
                      },
                    })}
                  />
                  <p className="text-xs text-[var(--color-text-muted)]">
                    Use flat fee for a fixed delivery charge. Per-km charging requires the restaurant GPS in Location and a pinned customer delivery location.
                  </p>
                  <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 text-xs text-[var(--color-text-secondary)]">
                    Delivery distance is calculated from the restaurant GPS in the Location tab to the customer's pinned delivery location.
                  </div>
                  <div className="space-y-3 rounded-[var(--radius-md)] border border-[var(--color-border-default)] p-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Delivery zones</h3>
                        <p className="mt-1 max-w-2xl text-xs text-[var(--color-text-muted)]">
                          Edit towns, aliases, fees, and minimums here. After saving, customer quotes and order checks use these zones. A blank zone fee uses the restaurant's existing fee. The minimum is checked against the discounted item subtotal, excluding delivery.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setDeliveryZones((zones) => [...zones, { town: '', aliases: '', postalCodes: '', deliveryFee: '', minimumOrder: '' }])}
                      >
                        <Plus className="mr-1 h-4 w-4" /> Add zone
                      </Button>
                    </div>
                    {deliveryZones.length === 0 ? (
                      <p className="text-xs text-[var(--color-text-muted)]">No delivery zones configured. The restaurant's existing delivery fee applies to all addresses.</p>
                    ) : (
                      <div className="space-y-3">
                        {deliveryZones.map((zone, index) => (
                          <div key={index} className="grid gap-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-3 sm:grid-cols-2 xl:grid-cols-[1.1fr_1.4fr_1.2fr_0.8fr_0.8fr_auto]">
                            <Input
                              label="Town / hamlet"
                              value={zone.town}
                              onChange={(event) => setDeliveryZones((zones) => zones.map((entry, row) => row === index ? { ...entry, town: event.target.value } : entry))}
                              placeholder="Credaro"
                            />
                            <Input
                              label="Aliases"
                              value={zone.aliases}
                              onChange={(event) => setDeliveryZones((zones) => zones.map((entry, row) => row === index ? { ...entry, aliases: event.target.value } : entry))}
                              placeholder="Frazione names, comma separated"
                            />
                            <Input
                              label="Postcodes"
                              value={zone.postalCodes}
                              onChange={(event) => setDeliveryZones((zones) => zones.map((entry, row) => row === index ? { ...entry, postalCodes: event.target.value } : entry))}
                              placeholder="Optional, comma separated"
                            />
                            <Input
                              label="Fee"
                              type="number"
                              min="0"
                              step="0.01"
                              value={zone.deliveryFee}
                              onChange={(event) => setDeliveryZones((zones) => zones.map((entry, row) => row === index ? { ...entry, deliveryFee: event.target.value } : entry))}
                              placeholder="Use default"
                            />
                            <Input
                              label="Minimum"
                              type="number"
                              min="0"
                              step="0.01"
                              value={zone.minimumOrder}
                              onChange={(event) => setDeliveryZones((zones) => zones.map((entry, row) => row === index ? { ...entry, minimumOrder: event.target.value } : entry))}
                              placeholder="No minimum"
                            />
                            <div className="flex items-end">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                aria-label={`Remove ${zone.town || `zone ${index + 1}`}`}
                                onClick={() => setDeliveryZones((zones) => zones.filter((_entry, row) => row !== index))}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {restaurantTab === 'payments' && (
                <div className="space-y-4">
                  <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 text-sm text-[var(--color-text-secondary)]">
                    Revolut card payments are optional and disabled by default. Sandbox mode uses the shared platform test keys from the backend environment; only production keys are stored per restaurant.
                  </div>
                  <label className="flex items-start gap-3 rounded-[var(--radius-md)] border border-[var(--color-border-default)] p-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-[var(--color-border-default)] accent-[var(--color-accent-amber)]"
                      {...form.register('onlinePaymentsEnabled')}
                    />
                    <span>
                      <span className="block text-sm font-medium text-[var(--color-text-primary)]">Enable online card payments</span>
                      <span className="block text-xs text-[var(--color-text-muted)]">Customers will see a card payment option after the selected Revolut secret and public keys are configured.</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 rounded-[var(--radius-md)] border border-[var(--color-border-default)] p-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-[var(--color-border-default)] accent-[var(--color-accent-amber)]"
                      {...form.register('onlinePaymentsOptional')}
                    />
                    <span>
                      <span className="block text-sm font-medium text-[var(--color-text-primary)]">Keep pay on arrival available</span>
                      <span className="block text-xs text-[var(--color-text-muted)]">When disabled, customers must pay by card once online payments are configured.</span>
                    </span>
                  </label>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-[var(--color-text-secondary)]">Revolut environment</label>
                    <select
                      {...form.register('revolutEnvironment')}
                      className="w-full h-10 px-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-default)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)] cursor-pointer"
                    >
                      <option value="sandbox">Sandbox</option>
                      <option value="production">Production</option>
                    </select>
                  </div>
                  <div className="grid gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 sm:grid-cols-2">
                    <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                      <span className={cn(
                        'h-2.5 w-2.5 rounded-full',
                        restaurant?.revolutSandboxPublicConfigured ? 'bg-[var(--color-accent-green)]' : 'bg-[var(--color-accent-red)]',
                      )} />
                      Sandbox public key
                      <span className="ml-auto text-xs font-medium text-[var(--color-text-muted)]">
                        {restaurant?.revolutSandboxPublicConfigured ? 'Configured' : 'Missing env'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
                      <span className={cn(
                        'h-2.5 w-2.5 rounded-full',
                        restaurant?.revolutSandboxConfigured ? 'bg-[var(--color-accent-green)]' : 'bg-[var(--color-accent-red)]',
                      )} />
                      Sandbox secret key
                      <span className="ml-auto text-xs font-medium text-[var(--color-text-muted)]">
                        {restaurant?.revolutSandboxConfigured ? 'Configured' : 'Missing env'}
                      </span>
                    </div>
                  </div>
                  <Input
                    label={`Production public key${restaurant?.revolutProductionPublicConfigured ? ' (configured)' : ''}`}
                    placeholder={restaurant?.revolutProductionPublicConfigured ? 'Leave blank to keep existing public key' : 'Paste Revolut production public key'}
                    autoComplete="off"
                    {...form.register('revolutProductionPublicKey')}
                  />
                  <Input
                    label={`Production secret key${restaurant?.revolutProductionConfigured ? ' (configured)' : ''}`}
                    type="password"
                    placeholder={restaurant?.revolutProductionConfigured ? 'Leave blank to keep existing secret key' : 'Paste Revolut production secret key'}
                    autoComplete="off"
                    {...form.register('revolutProductionApiKey')}
                  />
                </div>
              )}

              <Button type="submit" loading={updateRestaurant.isPending}>{t('settings.identity.saveChanges')}</Button>
            </form>
          </CardContent>
        </Card>
      )}
    </motion.div>
  )
}

// ─── Public Booking Link (slug) ───────────────────────────────────────────────

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/
const SLUG_MIN = 3
const SLUG_MAX = 60

function PublicLinkCard({ restaurant }: { restaurant: any }) {
  const updateRestaurant = useUpdateRestaurant()
  const [editOpen, setEditOpen] = useState(false)

  if (!restaurant) return null

  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const fullUrl = `${origin}/book/${restaurant.slug ?? ''}`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl)
      toast.success('Link copied')
    } catch {
      toast.error('Could not copy')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-[var(--color-accent-amber)]" />
          Public booking link
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-[var(--color-text-muted)] mb-3">
          Customers visit this URL to book a table or order. Share it on your website,
          social profiles, and printed materials.
        </p>

        <div className="flex items-center gap-2 mb-3">
          <code className="flex-1 truncate rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)] font-mono">
            {fullUrl}
          </code>
          <Button variant="secondary" size="sm" onClick={copy} title="Copy URL">
            <Copy className="h-4 w-4" />
          </Button>
        </div>

        <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
          Change link
        </Button>
      </CardContent>

      <SlugEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        restaurant={restaurant}
        origin={origin}
        updateRestaurant={updateRestaurant}
      />
    </Card>
  )
}

function SlugEditModal({
  open,
  onClose,
  restaurant,
  origin,
  updateRestaurant,
}: {
  open: boolean
  onClose: () => void
  restaurant: any
  origin: string
  updateRestaurant: ReturnType<typeof useUpdateRestaurant>
}) {
  const [value, setValue] = useState<string>(restaurant?.slug ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setValue(restaurant?.slug ?? '')
      setError(null)
    }
  }, [open, restaurant?.slug])

  const sanitize = (raw: string) =>
    raw
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')          // spaces → hyphens
      .replace(/[^a-z0-9-]/g, '')    // strip everything except a-z, 0-9, -
      .replace(/-+/g, '-')           // collapse repeated hyphens
      .replace(/^-+|-+$/g, '')       // strip leading/trailing hyphens

  const validate = (v: string): string | null => {
    if (v.length < SLUG_MIN) return `Must be at least ${SLUG_MIN} characters`
    if (v.length > SLUG_MAX) return `Must be at most ${SLUG_MAX} characters`
    if (!SLUG_PATTERN.test(v)) return 'Use lowercase letters, numbers, and hyphens only'
    return null
  }

  const onChange = (raw: string) => {
    const cleaned = sanitize(raw)
    setValue(cleaned)
    setError(cleaned.length === 0 ? null : validate(cleaned))
  }

  const unchanged = value === restaurant?.slug

  const onSave = async () => {
    const validationError = validate(value)
    if (validationError) {
      setError(validationError)
      return
    }
    if (unchanged) {
      onClose()
      return
    }
    setSaving(true)
    try {
      await new Promise<void>((resolve, reject) => {
        updateRestaurant.mutate(
          { documentId: restaurant.documentId, slug: value } as any,
          {
            onSuccess: () => resolve(),
            onError: (err: any) => reject(err),
          },
        )
      })
      toast.success('Public link updated')
      onClose()
    } catch (err: any) {
      // Strapi UID-uniqueness violation comes back as 400 with details mentioning
      // the field. Surface a clear message either way.
      const msg = err?.message ?? ''
      if (/unique|exists|taken|conflict/i.test(msg) || err?.status === 400) {
        setError('That link is already taken — try a different one')
      } else {
        setError('Could not save. Please try again.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Change public booking link">
      <div className="space-y-4">
        <div className="rounded-[var(--radius-md)] border border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)] p-3 flex gap-2">
          <AlertTriangle className="h-4 w-4 text-[var(--color-accent-amber)] shrink-0 mt-0.5" />
          <div className="text-xs text-[var(--color-text-primary)] leading-relaxed">
            <strong>Heads-up:</strong> changing this link will break any URLs
            you've already shared (printed menus, social posts, QR codes).
            Update them everywhere after saving.
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-[var(--color-text-secondary)]">New link</label>
          <div className="flex items-center rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] focus-within:border-[var(--color-accent-amber)]">
            <span className="px-3 py-2 text-sm text-[var(--color-text-muted)] font-mono whitespace-nowrap border-r border-[var(--color-border-subtle)]">
              {origin}/book/
            </span>
            <input
              autoFocus
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="my-restaurant"
              className="flex-1 bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)] focus:outline-none font-mono"
              maxLength={SLUG_MAX}
            />
          </div>
          {error ? (
            <p className="text-xs text-[var(--color-accent-red)]">{error}</p>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)]">
              Lowercase letters, numbers, and hyphens. {SLUG_MIN}–{SLUG_MAX} characters.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={onSave}
            loading={saving}
            disabled={!!error || value.length === 0 || unchanged}
          >
            Save link
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Ticket Logo (printed on receipts) ────────────────────────────────────────

function TicketLogoCard({ restaurant }: { restaurant: any }) {
  const updateRestaurant = useUpdateRestaurant()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const initialised = useRef(false)

  useEffect(() => {
    if (!restaurant || initialised.current) return
    initialised.current = true
    if (restaurant.ticketLogo?.url) {
      const url: string = restaurant.ticketLogo.url
      setPreview(url.startsWith('http') ? url : `${STRAPI_BASE_URL}${url}`)
    }
  }, [restaurant?.documentId])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.type !== 'image/png') {
      toast.error('Ticket logo must be a PNG file')
      e.target.value = ''
      return
    }
    setFile(f)
    const reader = new FileReader()
    reader.onload = (ev) => setPreview(ev.target?.result as string)
    reader.readAsDataURL(f)
  }

  const save = async () => {
    if (!restaurant) return
    try {
      setUploading(true)
      const id = file ? await uploadImage(file) : restaurant.ticketLogo?.id ?? null
      await new Promise<void>((resolve, reject) =>
        updateRestaurant.mutate(
          { documentId: restaurant.documentId, ticketLogo: id } as any,
          { onSuccess: () => resolve(), onError: () => reject() },
        ),
      )
      setFile(null)
      toast.success('Ticket logo saved')
    } catch {
      toast.error('Failed to save ticket logo')
    } finally {
      setUploading(false)
    }
  }

  const remove = async () => {
    if (!restaurant) return
    try {
      setUploading(true)
      await new Promise<void>((resolve, reject) =>
        updateRestaurant.mutate(
          { documentId: restaurant.documentId, ticketLogo: null } as any,
          { onSuccess: () => resolve(), onError: () => reject() },
        ),
      )
      setFile(null)
      setPreview(null)
      toast.success('Ticket logo removed')
    } catch {
      toast.error('Failed to remove ticket logo')
    } finally {
      setUploading(false)
    }
  }

  const dirty = file !== null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-[var(--color-accent-amber)]" />
          Ticket logo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-[var(--color-text-muted)] mb-3">
          Printed at the top of receipts. PNG only — transparent or white background, ideally
          ≤512px wide and high contrast (thermal printers print monochrome).
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/png"
          className="hidden"
          onChange={handleChange}
        />
        {preview ? (
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-white p-4 flex items-center justify-center">
            <img src={preview} alt="Ticket logo" className="max-h-32 w-auto object-contain" />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full flex flex-col items-center justify-center gap-2 h-32 rounded-[var(--radius-lg)] border-2 border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-amber)]/50 transition-colors cursor-pointer"
          >
            <Upload className="h-5 w-5" />
            <span className="text-sm">Click to upload PNG</span>
          </button>
        )}
        <div className="flex justify-end gap-2 mt-3">
          {preview && (
            <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
              <Upload className="h-3.5 w-3.5 mr-1" /> Change
            </Button>
          )}
          {(preview || restaurant?.ticketLogo) && (
            <Button variant="secondary" size="sm" onClick={remove} disabled={uploading}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
            </Button>
          )}
          <Button size="sm" onClick={save} loading={uploading} disabled={!dirty}>
            Save logo
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Features Tab ──────────────────────────────────────────────────────────────

function PrinterSetupCard({ restaurant }: { restaurant: any }) {
  const method = usePOSPrinterStore((s) => s.method)
  const receiptWidth = usePOSPrinterStore((s) => s.receiptWidth)
  const autoPrintOnPayment = usePOSPrinterStore((s) => s.autoPrintOnPayment)
  const lastDetectedAt = usePOSPrinterStore((s) => s.lastDetectedAt)
  const lastPrinterLabel = usePOSPrinterStore((s) => s.lastPrinterLabel)
  const setMethod = usePOSPrinterStore((s) => s.setMethod)
  const setReceiptWidth = usePOSPrinterStore((s) => s.setReceiptWidth)
  const setAutoPrintOnPayment = usePOSPrinterStore((s) => s.setAutoPrintOnPayment)
  const setLastPrinter = usePOSPrinterStore((s) => s.setLastPrinter)
  const [capabilities, setCapabilities] = useState(() => detectPrinterCapabilities())
  const [testing, setTesting] = useState(false)

  const refreshCapabilities = () => {
    setCapabilities(detectPrinterCapabilities())
    toast.success('Printer capabilities refreshed')
  }

  const testPrint = async () => {
    const sampleOrder: POSOrder = {
      id: -1,
      documentId: 'sample-receipt',
      bookosId: 'sample-receipt',
      status: 'paid',
      covers: 2,
      notes: '',
      openedAt: new Date().toISOString(),
      closedAt: new Date().toISOString(),
      serviceMode: 'counter',
      table: null,
      booking: null,
      orderItems: [
        {
          id: -1,
          documentId: 'sample-item-1',
          quantity: 1,
          unitPrice: 12,
          taxRate: 0.2,
          modifiers: null,
          course: 'main',
          seatNumber: null,
          notes: '',
          status: 'served',
          sentToKitchenAt: null,
          servedAt: null,
          menuItem: {
            id: -1,
            documentId: 'sample-burger',
            bookosId: 'sample-burger',
            name: 'Sample Burger',
            description: '',
            basePrice: 12,
            taxClass: 'standard',
            isAvailable: true,
            stockTracking: false,
            stockCount: null,
            prepStation: 'kitchen',
            imageUrl: null,
            sortOrder: 1,
            category: null,
            variants: [],
            modifiers: [],
            dietaryTags: [],
          },
          variant: null,
        },
      ],
      payments: [
        {
          id: -1,
          documentId: 'sample-payment',
          bookosId: 'sample-payment',
          amount: 14.4,
          tenderType: 'cash',
          gatewayRef: null,
          status: 'completed',
          tipAmount: 0,
          processedAt: new Date().toISOString(),
        },
      ],
    }

    try {
      setTesting(true)
      const label = await printReceipt({
        method,
        width: receiptWidth,
        order: sampleOrder,
        restaurant,
        payment: {
          amountPaid: 14.4,
          tipAmount: 0,
          taxTotal: 2.4,
          change: 0,
          tenderType: 'cash',
        },
      })
      setLastPrinter(label)
      toast.success('Test receipt sent')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Test print failed')
    } finally {
      setTesting(false)
    }
  }

  const methods: Array<{
    value: POSPrinterMethod
    label: string
    icon: React.ElementType
    enabled: boolean
    hint: string
  }> = [
    {
      value: 'system',
      label: 'System dialog',
      icon: Monitor,
      enabled: capabilities.systemPrint,
      hint: 'Uses the operating system print dialog. Most reliable fallback.',
    },
    {
      value: 'webusb',
      label: 'USB thermal',
      icon: Usb,
      enabled: capabilities.webUsb,
      hint: 'Direct ESC/POS printing for compatible USB printers in Chromium.',
    },
    {
      value: 'webserial',
      label: 'Serial thermal',
      icon: Cable,
      enabled: capabilities.webSerial,
      hint: 'Direct ESC/POS printing for serial or USB-serial printers.',
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Printer className="h-4 w-4 text-[var(--color-accent-amber)]" />
          Receipt printer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3 text-xs text-[var(--color-text-secondary)]">
          Browsers cannot silently list installed OS printers. Select a direct printer method when the browser supports it, or use the system print dialog fallback.
        </div>

        <div className="grid gap-2 md:grid-cols-3">
          {methods.map(({ value, label, icon: Icon, enabled, hint }) => (
            <button
              key={value}
              type="button"
              disabled={!enabled}
              onClick={() => setMethod(value)}
              className={cn(
                'rounded-[var(--radius-md)] border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45',
                method === value
                  ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
                  : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] hover:border-[var(--color-border-default)]',
              )}
            >
              <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                <Icon className="h-4 w-4" />
                {label}
              </span>
              <span className="block text-xs text-[var(--color-text-muted)]">{hint}</span>
              {!enabled && (
                <span className="mt-2 block text-xs text-red-300">Not supported by this browser</span>
              )}
            </button>
          ))}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-[var(--color-text-secondary)]">Receipt width</span>
            <select
              value={receiptWidth}
              onChange={(e) => setReceiptWidth(e.target.value as POSReceiptWidth)}
              className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-amber)]"
            >
              <option value="80mm">80mm</option>
              <option value="58mm">58mm</option>
            </select>
          </label>

          <label className="flex items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-2">
            <span>
              <span className="block text-sm font-medium text-[var(--color-text-primary)]">Auto-print after payment</span>
              <span className="block text-xs text-[var(--color-text-muted)]">Runs only after a successful payment.</span>
            </span>
            <input
              type="checkbox"
              checked={autoPrintOnPayment}
              onChange={(e) => setAutoPrintOnPayment(e.target.checked)}
              className="h-4 w-4 accent-[var(--color-accent-amber)]"
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-[var(--color-text-muted)]">
            {lastPrinterLabel ? (
              <span>
                Last printer: <span className="font-medium text-[var(--color-text-secondary)]">{lastPrinterLabel}</span>
                {lastDetectedAt ? ` · ${new Date(lastDetectedAt).toLocaleString()}` : ''}
              </span>
            ) : (
              <span>No printer tested yet.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={refreshCapabilities}>
              Detect
            </Button>
            <Button size="sm" onClick={testPrint} loading={testing}>
              <Printer className="h-3.5 w-3.5" />
              Test print
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface FeatureToggleProps {
  icon: React.ElementType
  iconColor: string
  iconBg: string
  name: string
  description: string
  tooltip: string
  badge?: string
  enabled: boolean
  onToggle: () => void
}

function FeatureToggle({ icon: Icon, iconColor, iconBg, name, description, tooltip, badge, enabled, onToggle }: FeatureToggleProps) {
  return (
    <div className={cn(
      'flex items-center gap-4 p-4 rounded-[var(--radius-lg)] border transition-all',
      enabled
        ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
        : 'border-[var(--color-border-default)] bg-[var(--color-bg-elevated)]',
    )}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] ${iconBg}`}>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">{name}</span>
          {badge && (
            <span className="px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide rounded bg-[var(--color-accent-amber)] text-[#1a1714]">{badge}</span>
          )}
          <Tooltip content={tooltip} side="right" className="whitespace-normal w-72 max-w-sm text-left">
            <Info className="h-3.5 w-3.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] cursor-default" />
          </Tooltip>
        </div>
        <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">{description}</p>
      </div>
      <button
        onClick={onToggle}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${enabled ? 'bg-[var(--color-accent-amber)]' : 'bg-[var(--color-bg-base)]'}`}
      >
        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0'}`} />
      </button>
    </div>
  )
}

function FeaturesTab() {
  const { t } = useTranslation()
  const { data: resp, isLoading } = useMyRestaurant()
  const updateRestaurant = useUpdateRestaurant()
  const restaurant = Array.isArray((resp as any)?.data) ? (resp as any).data[0] : null

  const [sheAnswers, setSheAnswers] = useState(false)
  const [customerPage, setCustomerPage] = useState(false)
  const [pos, setPos] = useState(false)
  const [posMode, setPosMode] = useState<POSMode>('hybrid')
  const coversEnabled = usePOSPreferencesStore((s) => s.coversEnabled)
  const setCoversEnabled = usePOSPreferencesStore((s) => s.setCoversEnabled)
  const inputMode = usePOSPreferencesStore((s) => s.inputMode)
  const setInputMode = usePOSPreferencesStore((s) => s.setInputMode)
  const appRestaurantId = useAppStore((s) => s.restaurantId)
  const preferenceRestaurantId = restaurant?.documentId ?? appRestaurantId
  const restaurantPreferences = usePOSPreferencesStore((s) => s.restaurantPreferences)
  const posRestaurantPrefs = useMemo(
    () => getPOSRestaurantPreferences(restaurantPreferences, preferenceRestaurantId),
    [restaurantPreferences, preferenceRestaurantId],
  )
  const setOrderTypeToggleEnabled = usePOSPreferencesStore((s) => s.setOrderTypeToggleEnabled)
  const setOrderTypeOption = usePOSPreferencesStore((s) => s.setOrderTypeOption)

  useEffect(() => {
    if (restaurant) {
      setSheAnswers(restaurant.featureSheAnswers ?? false)
      setCustomerPage(restaurant.featureCustomerPage ?? false)
      setPos(restaurant.featurePOS ?? false)
      setPosMode(restaurant.posMode ?? 'hybrid')
    }
  }, [restaurant?.documentId])

  const save = () => {
    if (!restaurant) return
    updateRestaurant.mutate({
      documentId: restaurant.documentId,
      featureSheAnswers: sheAnswers,
      featureCustomerPage: customerPage,
      featurePOS: pos,
      posMode,
    } as any, {
      onSuccess: () => {
        useAppStore.getState().setFeatures({ sheAnswers, customerPage, pos, posMode })
        toast.success(t('settings.features.saved'))
      },
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 rounded-[var(--radius-lg)] bg-[var(--color-bg-elevated)] animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <motion.div {...fadeInUp} className="space-y-4">
      <Card>
        <CardHeader><CardTitle>{t('settings.features.title')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <FeatureToggle
            icon={Phone} iconColor="text-purple-400" iconBg="bg-purple-500/10"
            name="SheAnswers" badge="AI"
            description="AI voice agent that handles reservation calls automatically"
            tooltip="SheAnswers uses ElevenLabs AI voice technology to handle incoming phone reservations on your behalf, answering questions, checking availability, and booking tables 24/7 without any human involvement."
            enabled={sheAnswers} onToggle={() => setSheAnswers((v) => !v)}
          />
          <FeatureToggle
            icon={Globe} iconColor="text-blue-400" iconBg="bg-blue-500/10"
            name="Customer Page"
            description="A public booking page your guests can use to reserve online"
            tooltip="A hosted, branded web page where customers can discover your restaurant, browse availability, and make reservations directly without any third-party platform."
            enabled={customerPage} onToggle={() => setCustomerPage((v) => !v)}
          />
          <FeatureToggle
            icon={ShoppingCart} iconColor="text-green-400" iconBg="bg-green-500/10"
            name="POS System"
            description="Offline-first point-of-sale with menus, orders, and payments"
            tooltip="A full point-of-sale system built into your dashboard. Manage your menu, take table orders, process payments, and keep operating even when offline. Syncs automatically when back online."
            enabled={pos} onToggle={() => setPos((v) => !v)}
          />
          {pos && (
            <div className="space-y-3 rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-4">
              <div>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">POS operating mode</p>
                <p className="text-xs text-[var(--color-text-muted)]">
                  Choose whether POS is tied to table reservations, standalone walk-ins, or both.
                </p>
              </div>
              <div className="grid gap-2 md:grid-cols-3">
                {[
                  { value: 'hybrid' as const, label: 'Hybrid', hint: 'Tables plus counter sales.' },
                  { value: 'integrated' as const, label: 'Reservations + tables', hint: 'Orders start from floor/table flow.' },
                  { value: 'standalone' as const, label: 'Standalone POS', hint: 'Counter sales without table setup.' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPosMode(option.value)}
                    className={cn(
                      'rounded-[var(--radius-md)] border p-3 text-left transition-colors',
                      posMode === option.value
                        ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
                        : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-default)]',
                    )}
                  >
                    <span className="block text-sm font-semibold text-[var(--color-text-primary)]">{option.label}</span>
                    <span className="mt-1 block text-xs text-[var(--color-text-muted)]">{option.hint}</span>
                  </button>
                ))}
              </div>

              <div className="border-t border-[var(--color-border-subtle)] pt-3">
                <div className="mb-3">
                  <p className="text-sm font-semibold text-[var(--color-text-primary)]">This device is a</p>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    This preference is saved only on this device and controls how POS text and number fields work.
                  </p>
                </div>
                <div className="mb-3 grid gap-2 sm:grid-cols-2">
                  {[
                    { value: 'laptop' as const, label: 'Laptop', hint: 'Use the physical or system keyboard.', icon: Laptop },
                    { value: 'touchscreen' as const, label: 'Touch screen', hint: 'Show the POS keyboard and number pad.', icon: Tablet },
                  ].map(({ value, label, hint, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setInputMode(value)}
                      aria-pressed={inputMode === value}
                      className={cn(
                        'flex items-start gap-3 rounded-[var(--radius-md)] border p-3 text-left transition-colors',
                        inputMode === value
                          ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
                          : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] hover:border-[var(--color-border-default)]',
                      )}
                    >
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-accent-amber)]" />
                      <span>
                        <span className="block text-sm font-semibold text-[var(--color-text-primary)]">{label}</span>
                        <span className="mt-1 block text-xs text-[var(--color-text-muted)]">{hint}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <label className="flex items-center justify-between gap-4 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-3">
                  <span>
                    <span className="block text-sm font-semibold text-[var(--color-text-primary)]">Track covers in POS</span>
                    <span className="block text-xs text-[var(--color-text-muted)]">
                      When off, staff can open POS orders without choosing guest count. This is stored locally and does not change the database.
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={coversEnabled}
                    onChange={(e) => setCoversEnabled(e.target.checked)}
                    className="h-4 w-4 shrink-0 accent-[var(--color-accent-amber)]"
                  />
                </label>
              </div>

              <div className="space-y-3 border-t border-[var(--color-border-subtle)] pt-3">
                <label className="flex items-center justify-between gap-4 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-3">
                  <span>
                    <span className="block text-sm font-semibold text-[var(--color-text-primary)]">Show order type selector</span>
                    <span className="block text-xs text-[var(--color-text-muted)]">
                      Let staff choose table, takeaway, or delivery labels from the POS. Labels are stored locally per restaurant.
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={posRestaurantPrefs.orderTypeToggleEnabled}
                    onChange={(e) => setOrderTypeToggleEnabled(preferenceRestaurantId, e.target.checked)}
                    className="h-4 w-4 shrink-0 accent-[var(--color-accent-amber)]"
                  />
                </label>

                {posRestaurantPrefs.orderTypeToggleEnabled && (
                  <div className="space-y-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-3">
                    <div>
                      <p className="text-sm font-semibold text-[var(--color-text-primary)]">Order type options</p>
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Enable only the choices this restaurant uses, and rename them to match the operation.
                      </p>
                    </div>
                    {posRestaurantPrefs.orderTypeOptions.map((option) => (
                      <div key={option.key} className="grid gap-2 rounded-[var(--radius-sm)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-2 sm:grid-cols-[minmax(0,1fr)_minmax(160px,220px)] sm:items-center">
                        <label className="flex min-w-0 items-center gap-2 text-sm font-medium text-[var(--color-text-primary)]">
                          <input
                            type="checkbox"
                            checked={option.enabled}
                            onChange={(e) =>
                              setOrderTypeOption(preferenceRestaurantId, option.key as POSOrderTypeKey, { enabled: e.target.checked })
                            }
                            className="h-4 w-4 shrink-0 accent-[var(--color-accent-amber)]"
                          />
                          <span className="truncate">{option.label}</span>
                        </label>
                        <Input
                          label="Label"
                          value={option.label}
                          onChange={(e) =>
                            setOrderTypeOption(preferenceRestaurantId, option.key as POSOrderTypeKey, { label: e.target.value })
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Button onClick={save} loading={updateRestaurant.isPending}>{t('settings.features.savePreferences')}</Button>
    </motion.div>
  )
}

// ─── API Keys Tab ──────────────────────────────────────────────────────────────

function AppearanceTab() {
  const mode = useThemeStore((s) => s.mode)
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  const setMode = useThemeStore((s) => s.setMode)

  const options: Array<{
    value: ThemeMode
    label: string
    icon: React.ElementType
    hint: string
  }> = [
    { value: 'light', label: 'Light', icon: Sun, hint: 'Bright interface for daytime work.' },
    { value: 'dark', label: 'Dark', icon: Moon, hint: 'Low-glare interface for long shifts.' },
    { value: 'system', label: 'System', icon: Monitor, hint: 'Follow the operating system setting.' },
  ]

  return (
    <motion.div {...fadeInUp} className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 md:grid-cols-3">
            {options.map(({ value, label, icon: Icon, hint }) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                className={cn(
                  'rounded-[var(--radius-md)] border p-4 text-left transition-colors',
                  mode === value
                    ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)]'
                    : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-border-default)]',
                )}
              >
                <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
                  <Icon className="h-4 w-4" />
                  {label}
                </span>
                <span className="block text-xs text-[var(--color-text-muted)]">{hint}</span>
              </button>
            ))}
          </div>

          <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-text-secondary)]">
            Current active theme: <span className="font-semibold capitalize text-[var(--color-text-primary)]">{resolvedTheme}</span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

function ApiKeysTab() {
  const { t } = useTranslation()
  const { data, isLoading } = useApiKeys()
  const generateKey = useGenerateApiKey()
  const revokeKey = useRevokeApiKey()
  const [revealedKey, setRevealedKey] = useState<string | null>(null)

  const keys = (data as any)?.data ?? []

  const handleGenerate = () => {
    generateKey.mutate(undefined, {
      onSuccess: (res: any) => setRevealedKey(res.plainKey),
    })
  }

  return (
    <motion.div {...fadeInUp} className="space-y-4">
      {/* One-time key reveal modal */}
      <Modal open={!!revealedKey} onClose={() => setRevealedKey(null)} title={t('settings.apiKeys.modalTitle')} size="sm">
        <p className="text-sm text-[var(--color-text-secondary)] mb-3">
          {t('settings.apiKeys.modalHint')}
        </p>
        <div className="flex items-center gap-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-bg-base)] border border-[var(--color-border-default)] mb-4">
          <code className="flex-1 text-sm font-mono text-[var(--color-accent-amber)] break-all">{revealedKey}</code>
          <Button variant="ghost" size="icon-sm" onClick={() => { navigator.clipboard.writeText(revealedKey!); toast.success(t('settings.apiKeys.copied')) }}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Button onClick={() => setRevealedKey(null)}>{t('settings.apiKeys.done')}</Button>
      </Modal>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>{t('settings.apiKeys.title')}</CardTitle>
          <Button size="sm" loading={generateKey.isPending} onClick={handleGenerate}>
            <Plus className="h-3.5 w-3.5" /> {t('settings.apiKeys.generateKey')}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => <div key={i} className="h-12 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] animate-pulse" />)}
            </div>
          ) : keys.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)] text-center py-4">{t('settings.apiKeys.noKeysYet')}</p>
          ) : (
            <div className="space-y-3">
              {keys.map((key: any) => (
                <div key={key.documentId} className="flex items-center gap-3 p-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)]">
                  <code className="flex-1 text-sm font-mono text-[var(--color-text-primary)]">••••••••{key.displaySuffix}</code>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">{t('settings.apiKeys.active')}</span>
                  <span className="text-xs text-[var(--color-text-muted)]">{new Date(key.createdAt).toLocaleDateString()}</span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    loading={revokeKey.isPending}
                    onClick={() => revokeKey.mutate(key.documentId, { onSuccess: () => toast.success(t('settings.apiKeys.revoked')) })}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--color-accent-red)]" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}

// ─── Usage Tab ─────────────────────────────────────────────────────────────────

const PLAN_META: Record<Plan, { label: string; color: string; bg: string; border: string }> = {
  free: { label: 'Free', color: 'text-[var(--color-text-secondary)]', bg: 'bg-[var(--color-bg-elevated)]', border: 'border-[var(--color-border-default)]' },
  pro: { label: 'Pro — €250/mo', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  enterprise: { label: 'Enterprise — €400/mo', color: 'text-[var(--color-accent-amber)]', bg: 'bg-[var(--color-accent-amber-dim)]', border: 'border-[var(--color-accent-amber)]' },
}

interface UsageBarProps {
  label: string
  used: number
  limit: number | null
  icon: React.ElementType
}

function UsageBar({ label, used, limit, icon: Icon }: UsageBarProps) {
  const pct = limit == null ? 0 : Math.min(100, Math.round((used / limit) * 100))
  const isUnlimited = limit == null
  const isWarning = !isUnlimited && pct >= 80
  const isFull = !isUnlimited && used >= limit

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 font-medium text-[var(--color-text-primary)]">
          <Icon className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
          {label}
        </span>
        <span className={cn(
          'text-xs font-mono',
          isFull ? 'text-red-400' : isWarning ? 'text-amber-400' : 'text-[var(--color-text-secondary)]',
        )}>
          {isUnlimited ? <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-400" /> Unlimited</span> : `${used} / ${limit}`}
        </span>
      </div>
      {!isUnlimited && (
        <div className="h-1.5 rounded-full bg-[var(--color-bg-base)] overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              isFull ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-[var(--color-accent-amber)]',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  )
}

function UsageTab() {
  const { data: resp, isLoading } = useMyUsage()
  const usage = (resp as any)?.data as import('@/lib/queries/useRestaurants').MyUsage | undefined

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-[var(--radius-lg)] bg-[var(--color-bg-elevated)] animate-pulse" />)}
      </div>
    )
  }

  if (!usage) return null

  const plan = usage.plan ?? 'free'
  const meta = PLAN_META[plan]
  const monthName = new Date(usage.currentPeriod.year, usage.currentPeriod.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })

  return (
    <motion.div {...fadeInUp} className="space-y-4">
      {/* Plan badge */}
      <Card>
        <CardContent className="pt-5">
          <div className={cn('flex items-center gap-3 p-4 rounded-[var(--radius-lg)] border', meta.bg, meta.border)}>
            <div className={cn('flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)]', meta.bg)}>
              {plan === 'enterprise' ? <Zap className={cn('h-5 w-5', meta.color)} /> : plan === 'pro' ? <Sparkles className={cn('h-5 w-5', meta.color)} /> : <BarChart2 className="h-5 w-5 text-[var(--color-text-muted)]" />}
            </div>
            <div>
              <p className="text-xs text-[var(--color-text-muted)] uppercase tracking-wide font-semibold">Current Plan</p>
              <p className={cn('text-lg font-bold', meta.color)}>{meta.label}</p>
            </div>
            {plan === 'free' && (
              <div className="ml-auto text-xs text-[var(--color-text-muted)] bg-[var(--color-bg-base)] rounded-[var(--radius-md)] px-3 py-1.5 border border-[var(--color-border-subtle)]">
                Contact us to upgrade
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Monthly usage */}
      <Card>
        <CardHeader>
          <CardTitle>Usage — {monthName}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <UsageBar
            label="Manual bookings"
            used={usage.usage.manualBookings.used}
            limit={usage.usage.manualBookings.limit}
            icon={Key}
          />
          <UsageBar
            label="Online booking page"
            used={usage.usage.customerPageBookings.used}
            limit={usage.usage.customerPageBookings.limit}
            icon={Globe}
          />
          <UsageBar
            label="SheAnswers test calls"
            used={usage.usage.sheAnswersTestCalls.used}
            limit={usage.usage.sheAnswersTestCalls.limit}
            icon={Phone}
          />
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader><CardTitle>Included features</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Phone number', ok: usage.features.phoneNumberAllowed },
              { label: 'WhatsApp number', ok: usage.features.whatsappAllowed },
              { label: 'POS system', ok: usage.features.posEnabled },
              {
                label: usage.features.sheAnswersMonthlyCallCap == null
                  ? 'SheAnswers calls (unlimited)'
                  : usage.features.sheAnswersMonthlyCallCap === 0
                  ? 'SheAnswers phone calls'
                  : `SheAnswers calls (${usage.features.sheAnswersMonthlyCallCap}/mo)`,
                ok: usage.features.sheAnswersMonthlyCallCap !== 0,
              },
            ].map(({ label, ok }) => (
              <div key={label} className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-[var(--radius-md)] border text-sm',
                ok ? 'border-green-500/30 bg-green-500/5 text-green-400' : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]',
              )}>
                {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <Lock className="h-4 w-4 shrink-0" />}
                {label}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

// ─── Notifications Tab ─────────────────────────────────────────────────────────

function NotificationsTab() {
  const { t } = useTranslation()
  const { data: resp } = useMyRestaurant()
  const updateRestaurant = useUpdateRestaurant()
  const restaurant = Array.isArray((resp as any)?.data) ? (resp as any).data[0] : null

  const [emailOnBooking, setEmailOnBooking] = useState(true)
  const [smsOnChange, setSmsOnChange] = useState(false)

  useEffect(() => {
    if (restaurant) {
      setEmailOnBooking(restaurant.notifyEmailOnBooking ?? true)
      setSmsOnChange(restaurant.notifySmsOnChange ?? false)
    }
  }, [restaurant?.documentId])

  const save = () => {
    if (!restaurant) return
    updateRestaurant.mutate({
      documentId: restaurant.documentId,
      notifyEmailOnBooking: emailOnBooking,
      notifySmsOnChange: smsOnChange,
    } as any, {
      onSuccess: () => toast.success(t('settings.notifications.saved')),
    })
  }

  return (
    <motion.div {...fadeInUp}>
      <Card>
        <CardHeader><CardTitle>{t('settings.notifications.title')}</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label className="flex items-center justify-between p-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] cursor-pointer">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">{t('settings.notifications.emailOnBooking')}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{t('settings.notifications.emailOnBookingHint')}</p>
              </div>
              <input type="checkbox" checked={emailOnBooking} onChange={(e) => setEmailOnBooking(e.target.checked)} className="accent-[var(--color-accent-amber)] h-4 w-4" />
            </label>
            <label className="flex items-center justify-between p-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] cursor-pointer">
              <div>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">{t('settings.notifications.smsOnChange')}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{t('settings.notifications.smsOnChangeHint')}</p>
              </div>
              <input type="checkbox" checked={smsOnChange} onChange={(e) => setSmsOnChange(e.target.checked)} className="accent-[var(--color-accent-amber)] h-4 w-4" />
            </label>
            <Button onClick={save} loading={updateRestaurant.isPending}>{t('settings.notifications.savePreferences')}</Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
